import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/apiClient";
import { AdminHeader, AdminPage } from "@/components/admin/AdminUI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { UploadCloud, Trash2, Pencil, Sparkles, AlertTriangle, Eye, Loader2 } from "lucide-react";

const CATS = ["Representations", "Government Communications", "Court Orders", "Judgments", "Reports",
  "Research", "Public Notices", "Institutional Documents", "Other Resources"];
const BLANK = { title: "", category: "Representations", doc_date: "", authority: "", reference_no: "",
  subject: "", language: "", description: "", needs_review: false, file_id: null, is_public: true };

function Field({ label, flag, children }) {
  return <div className="space-y-1.5"><label className="label-eyebrow text-slate-500 flex items-center gap-2">{label}{flag && <span className="text-[9px] text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded normal-case tracking-normal">verify</span>}</label>{children}</div>;
}

export default function DocumentsAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(BLANK);
  const [fileName, setFileName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiNote, setAiNote] = useState("");
  const fileRef = useRef();
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const load = () => api.get("/documents?include_unpublished=true&limit=300").then(({ data }) => setItems(data.items)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setF(BLANK); setFileName(""); setAiNote(""); setOpen(true); };
  const openEdit = (d) => { setEditing(d.id); setF({ ...BLANK, ...d }); setFileName(d.storage_path ? "Current file attached" : ""); setAiNote(""); setOpen(true); };

  const analyze = async (file) => {
    if (!file) return;
    setUploading(true); setAiNote("");
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/documents/analyze", fd);
      const a = data.analysis || {};
      setF((p) => ({ ...p,
        file_id: data.file.id,
        reference_no: a.reference_no || p.reference_no,
        doc_date: a.date_of_representation || p.doc_date,
        subject: a.subject || p.subject,
        authority: a.authority || p.authority,
        language: a.language || p.language,
        description: a.summary || p.description,
        title: p.title || a.subject || a.reference_no || file.name.replace(/\.[^.]+$/, ""),
        needs_review: !!a.needs_review || !!a._ai_error,
      }));
      setFileName(file.name);
      if (a._ai_error) setAiNote(a._ai_error);
      else setAiNote(`AI extracted the details${data.ocr_used ? " (via OCR)" : ""}. Please review before publishing.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload/analyze failed"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!editing && !f.file_id) return toast.error("Upload a representation PDF first");
    if (!f.subject && !f.title) return toast.error("A subject or title is required");
    setSaving(true);
    try {
      if (editing) {
        const { file_id, storage_path, ext, id, slug, created_at, ...body } = f;
        await api.put(`/documents/${editing}`, body);
        toast.success("Representation updated");
      } else {
        await api.post("/documents", { ...f, title: f.title || f.subject });
        toast.success("Representation added");
      }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const togglePublish = async (d) => {
    try { await api.put(`/documents/${d.id}`, { is_public: !d.is_public }); load(); }
    catch { toast.error("Failed"); }
  };
  const del = async (d) => { if (!window.confirm("Delete this representation?")) return; await api.delete(`/documents/${d.id}`); toast.success("Deleted"); load(); };

  return (
    <>
      <AdminHeader title="Document Centre" subtitle={`${items.length} representations`} actions={<Button onClick={openAdd} className="bg-npst-navy text-white gap-2" data-testid="add-document-btn"><UploadCloud size={16} /> Upload Representation</Button>} />
      <AdminPage>
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200"><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">Reference / Subject</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Authority</th><th className="px-4 py-3">Published</th><th className="px-4 py-3 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {items.length === 0 ? <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No representations yet. Click “Upload Representation”.</td></tr>
                : items.map((d) => (
                  <tr key={d.id} className="border-b border-slate-100" data-testid="admin-doc-row">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {d.reference_no && <span className="font-mono text-[11px] text-npst-gold">{d.reference_no}</span>}
                        {d.needs_review && <AlertTriangle size={13} className="text-amber-500" title="Needs verification" />}
                      </div>
                      <div className="font-medium text-npst-ink">{d.subject || d.title}</div>
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-slate-500">{d.doc_date}</td>
                    <td className="px-4 py-3 text-xs text-slate-500 max-w-[220px] truncate">{d.authority}</td>
                    <td className="px-4 py-3"><Switch checked={!!d.is_public} onCheckedChange={() => togglePublish(d)} data-testid={`doc-publish-${d.id}`} /></td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-3">
                        {d.storage_path && <a href={fileUrl(d.storage_path)} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-npst-navy" title="View PDF"><Eye size={15} /></a>}
                        <button onClick={() => openEdit(d)} className="text-slate-400 hover:text-npst-navy" data-testid={`doc-edit-${d.id}`}><Pencil size={15} /></button>
                        <button onClick={() => del(d)} className="text-slate-400 hover:text-red-600" data-testid={`doc-delete-${d.id}`}><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </AdminPage>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? "Edit Representation" : "Upload Representation"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {!editing && (
              <>
                <button onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-slate-300 rounded-lg py-6 hover:border-npst-gold flex flex-col items-center gap-1 text-slate-500" data-testid="doc-upload-zone">
                  {uploading ? <Loader2 size={22} className="animate-spin text-npst-gold" /> : <UploadCloud size={22} />}
                  <span className="text-sm">{uploading ? "Reading & extracting…" : fileName || "Upload representation PDF (English / Hindi / Kruti Dev / scanned)"}</span>
                </button>
                <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => analyze(e.target.files[0])} data-testid="doc-file-input" />
                {aiNote && <div className="flex items-start gap-2 text-xs bg-npst-goldLight/40 border border-npst-goldBorder/40 text-npst-ink px-3 py-2 rounded" data-testid="doc-ai-note"><Sparkles size={13} className="text-npst-gold mt-0.5 shrink-0" /> {aiNote}</div>}
              </>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Reference / Letter No." flag={f.needs_review && !f.reference_no}><Input value={f.reference_no || ""} onChange={(e) => set("reference_no", e.target.value)} data-testid="doc-reference" /></Field>
              <Field label="Date" flag={f.needs_review && !f.doc_date}><Input value={f.doc_date || ""} onChange={(e) => set("doc_date", e.target.value)} placeholder="e.g. 2026-02-12" data-testid="doc-date" /></Field>
            </div>
            <Field label="Subject / Caption" flag={f.needs_review && !f.subject}><Textarea rows={2} value={f.subject || ""} onChange={(e) => set("subject", e.target.value)} data-testid="doc-subject" /></Field>
            <Field label="Authority / Department Addressed To" flag={f.needs_review && !f.authority}><Input value={f.authority || ""} onChange={(e) => set("authority", e.target.value)} data-testid="doc-authority" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Language"><Select value={f.language || ""} onValueChange={(v) => set("language", v)}><SelectTrigger data-testid="doc-language"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{["English", "Hindi", "Mixed"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></Field>
              <Field label="Category"><Select value={f.category} onValueChange={(v) => set("category", v)}><SelectTrigger data-testid="doc-category"><SelectValue /></SelectTrigger><SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></Field>
            </div>
            <Field label="Summary / Description"><Textarea rows={2} value={f.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="doc-description" /></Field>
            <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
              <span className="text-sm text-npst-ink">Publish on public Document Center</span>
              <Switch checked={!!f.is_public} onCheckedChange={(v) => set("is_public", v)} data-testid="doc-is-public" />
            </div>
            <Button onClick={save} disabled={saving} className="w-full bg-npst-navy text-white gap-2" data-testid="doc-save-btn">{saving ? <Loader2 size={15} className="animate-spin" /> : null}{editing ? "Save Changes" : "Add to Repository"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
